import { Injectable } from '@angular/core';
import { fakeText } from './privacy-fake-data';

/** Elements with the private data (the same as the blur in assets/scss/core/privacy-mode.scss). */
const PRIVATE_SELECTOR =
  ".pii, a[href^='tel:'], a[href^='mailto:'], .pii-select .ng-value-label, .pii-select .ng-option-label";

interface ReplacedText {
  original: string;
  fake: string;
}

/**
 * Replaces the texts of the private elements with fake data and puts the original back when it is stopped.
 * Only the text nodes are changed (Angular does not know about it and does not overwrite them until its value
 * changes), the new and changed nodes are replaced by an observer.
 */
@Injectable({ providedIn: 'root' })
export class PrivacyAnonymizerService {
  private readonly replaced = new WeakMap<Text, ReplacedText>();
  private observer: MutationObserver | null = null;

  start(): void {
    if (this.observer) return;

    this.observer = new MutationObserver(records => records.forEach(record => this.handle(record)));
    this.observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    this.forEachPrivateText(document.body, node => this.anonymize(node));
  }

  stop(): void {
    if (!this.observer) return;

    this.observer.disconnect();
    this.observer = null;
    this.forEachPrivateText(document.body, node => this.restore(node));
  }

  private handle(record: MutationRecord): void {
    if (record.type === 'characterData') {
      this.handleText(record.target as Text);
      return;
    }

    record.addedNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        this.handleText(node as Text);
      } else if (node instanceof Element) {
        if (node.closest(PRIVATE_SELECTOR)) {
          this.walk(node, text => this.anonymize(text));
        } else {
          this.forEachPrivateText(node, text => this.anonymize(text));
        }
      }
    });
  }

  private handleText(node: Text): void {
    if (node.parentElement?.closest(PRIVATE_SELECTOR)) this.anonymize(node);
  }

  private anonymize(node: Text): void {
    const known = this.replaced.get(node);
    if (known?.fake === node.data || !node.data.trim()) return;

    const fake = fakeText(node.data);
    if (fake === node.data) return;

    this.replaced.set(node, { original: node.data, fake });
    node.data = fake;
  }

  private restore(node: Text): void {
    const known = this.replaced.get(node);
    if (known && known.fake === node.data) node.data = known.original;
    this.replaced.delete(node);
  }

  private forEachPrivateText(root: Element, callback: (node: Text) => void): void {
    root.querySelectorAll(PRIVATE_SELECTOR).forEach(element => this.walk(element, callback));
  }

  private walk(root: Element, callback: (node: Text) => void): void {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode()) nodes.push(node as Text);
    nodes.forEach(callback);
  }
}
